import { useMemo } from "react";
import {
  ExternalLinkIcon,
  HomeIcon,
  PencilIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "@heroicons/react/outline";

import { Action } from "@/components/ui/DropdownMenu";
import { WorkspaceActionId } from "@/enums/workspace-action-id";
import { Workspace } from "@/types/workspaces";
import { User } from "@/types/user";
import { useIsOrgOwnerById } from "@/hooks/organizations";
import { useOrganizationSubscriptionData } from "@/providers";

interface UseWorkspaceActionsParams {
  currentUser?: User;
  onEditName: (workspace: Workspace) => void;
  onAddMember: (workspace: Workspace) => void;
  onSetDefault: (workspace: Workspace, organizationMemberId?: number) => void;
  onSetFallbackOwner?: (workspace: Workspace) => void;
  accessibleWorkspaces?: { workspaces: Workspace[] };
  organizationId?: number;
}

export const useWorkspaceActions = ({
  currentUser,
  onEditName,
  onAddMember,
  onSetDefault,
  onSetFallbackOwner,
  accessibleWorkspaces,
  organizationId,
}: UseWorkspaceActionsParams) => {
  const activeOrganization = currentUser?.memberships.organizations.find(
    org => org.organization_id === organizationId,
  );
  const defaultWorkspaceId = activeOrganization?.default_workspace_id;
  const isOrgOwner = useIsOrgOwnerById(organizationId ?? 0);
  const { isPLAdmin } = useOrganizationSubscriptionData();

  const actions: Action<Workspace>[] = useMemo(
    () => [
      {
        id: WorkspaceActionId.OPEN_IN_NEW_TAB,
        label: "Open in New Tab",
        icon: ExternalLinkIcon,
        onClick: (workspace?: Workspace) => {
          if (workspace) {
            window.open(`/workspace/${workspace.id}/home`, "_blank");
          }
        },
        getDisabled: (workspace?: Workspace) => {
          if (!workspace) {
            return false;
          }

          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);

          if (!hasAccess) {
            return true;
          }

          return !workspace.admin_user_ids.includes(currentUser?.id || 0);
        },
      },
      {
        id: WorkspaceActionId.SET_DEFAULT,
        label: "Set as Default",
        icon: HomeIcon,
        getDisabled: (workspace?: Workspace) => {
          if (!workspace) {
            return false;
          }

          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);

          if (!hasAccess) {
            return true;
          }

          return workspace.id === defaultWorkspaceId;
        },
        onClick: (workspace?: Workspace) => {
          if (workspace && activeOrganization) {
            onSetDefault(workspace, activeOrganization.id);
          }
        },
      },
      {
        id: WorkspaceActionId.CHANGE_NAME,
        label: "Change Name",
        icon: PencilIcon,
        getDisabled: (workspace?: Workspace) => {
          if (!workspace) {
            return false;
          }

          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);

          if (!hasAccess) {
            return true;
          }

          return !workspace.admin_user_ids.includes(currentUser?.id || 0);
        },
        onClick: (workspace?: Workspace) => {
          if (workspace) {
            onEditName(workspace);
          }
        },
      },
      {
        id: WorkspaceActionId.ADD_MEMBER,
        label: "Add Member",
        icon: UsersIcon,
        getDisabled: (workspace?: Workspace) => {
          if (!workspace) {
            return false;
          }

          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);

          if ((!hasAccess || !isOrgOwner) && !isPLAdmin) {
            return true;
          }

          return !workspace.admin_user_ids.includes(currentUser?.id || 0);
        },
        onClick: (workspace?: Workspace) => {
          if (workspace) {
            onAddMember(workspace);
          }
        },
      },
      {
        id: WorkspaceActionId.FALLBACK_OWNER,
        label: "Set Fallback Owner",
        icon: ShieldCheckIcon,
        getDisabled: (workspace?: Workspace) => {
          if (!workspace || !onSetFallbackOwner) {
            return true;
          }
          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);
          if (!hasAccess) {
            return true;
          }
          return !workspace.admin_user_ids.includes(currentUser?.id || 0);
        },
        onClick: (workspace?: Workspace) => {
          if (workspace && onSetFallbackOwner) {
            onSetFallbackOwner(workspace);
          }
        },
      },
    ],
    [
      currentUser,
      onEditName,
      onAddMember,
      onSetDefault,
      onSetFallbackOwner,
      accessibleWorkspaces,
      isOrgOwner,
      isPLAdmin,
      defaultWorkspaceId,
      activeOrganization,
    ],
  );

  const contextMenuActions = useMemo(
    () => [
      {
        id: WorkspaceActionId.OPEN_IN_NEW_TAB,
        label: "Open in New Tab",
        icon: ExternalLinkIcon,
        onClick: (workspace?: Workspace) => {
          if (workspace) {
            window.open(`/workspace/${workspace.id}/home`, "_blank");
          }
        },
        getDisabled: (workspace?: Workspace) => {
          if (!workspace) {
            return false;
          }

          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);

          if (!hasAccess) {
            return true;
          }

          return !workspace.admin_user_ids.includes(currentUser?.id || 0);
        },
      },
      {
        id: WorkspaceActionId.CHANGE_NAME,
        label: "Change Name",
        icon: PencilIcon,
        getDisabled: (workspace?: Workspace) => {
          if (!workspace) {
            return false;
          }

          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);

          if (!hasAccess) {
            return true;
          }

          return !workspace.admin_user_ids.includes(currentUser?.id || 0);
        },
        onClick: (workspace?: Workspace) => {
          if (workspace) {
            onEditName(workspace);
          }
        },
      },
      {
        id: WorkspaceActionId.SET_DEFAULT,
        label: "Set as Default",
        icon: HomeIcon,
        getDisabled: (workspace?: Workspace) => {
          if (!workspace) {
            return false;
          }

          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);

          if (!hasAccess) {
            return true;
          }

          return workspace.id === defaultWorkspaceId;
        },
        onClick: (workspace?: Workspace) => {
          if (workspace) {
            onSetDefault(workspace, activeOrganization?.id);
          }
        },
      },
      {
        id: WorkspaceActionId.ADD_MEMBER,
        label: "Add Member",
        icon: UsersIcon,
        getDisabled: (workspace?: Workspace) => {
          if (!workspace) {
            return false;
          }

          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);

          if ((!hasAccess || !isOrgOwner) && !isPLAdmin) {
            return true;
          }

          return !workspace.admin_user_ids.includes(currentUser?.id || 0);
        },
        onClick: (workspace?: Workspace) => {
          if (workspace) {
            onAddMember(workspace);
          }
        },
      },
      {
        id: WorkspaceActionId.FALLBACK_OWNER,
        label: "Set Fallback Owner",
        icon: ShieldCheckIcon,
        getDisabled: (workspace?: Workspace) => {
          if (!workspace || !onSetFallbackOwner) {
            return true;
          }
          const hasAccess = accessibleWorkspaces?.workspaces.some(w => w.id === workspace.id);
          if (!hasAccess) {
            return true;
          }
          return !workspace.admin_user_ids.includes(currentUser?.id || 0);
        },
        onClick: (workspace?: Workspace) => {
          if (workspace && onSetFallbackOwner) {
            onSetFallbackOwner(workspace);
          }
        },
      },
    ],
    [
      currentUser,
      onEditName,
      onAddMember,
      onSetDefault,
      onSetFallbackOwner,
      accessibleWorkspaces,
      isOrgOwner,
      isPLAdmin,
      defaultWorkspaceId,
      activeOrganization?.id,
    ],
  );

  return {
    actions,
    contextMenuActions,
  };
};
